import { useState } from "react";
import {
  ChevronRight,
  Maximize2,
  MousePointer2,
  Move,
  Search,
  Sliders,
  Ruler,
  CircleDot,
  Pencil,
  Square,
  Eraser,
  Undo2,
  Redo2,
  LayoutGrid,
  Play,
  SkipBack,
  SkipForward,
  Repeat,
  Share2,
  Send,
  Bell,
  MessageSquare,
} from "lucide-react";

/* ---------- Mock scan tiles ---------- */
const TILES = [
  { title: "Vue axe court", sub: "Coupe basale", img: "https://images.unsplash.com/photo-1559757175-7cb036e0d465?auto=format&fit=crop&w=600&q=60", n: "5/24", zoom: "140%", w: "440", l: "60", overlay: "cyan" },
  { title: "Vue axe court", sub: "Mi-ventriculaire", img: "https://images.unsplash.com/photo-1530497610245-94d3c16cda28?auto=format&fit=crop&w=600&q=60", n: "12/34", zoom: "80%", w: "520", l: "70" },
  { title: "Vue axe court", sub: "Coupe apicale", img: "https://images.unsplash.com/photo-1582719471384-894fbb16e074?auto=format&fit=crop&w=600&q=60", n: "2/12", zoom: "140%", w: "480", l: "60" },
  { title: "Vue coronale", sub: "Projection frontale", img: "https://images.unsplash.com/photo-1631815588090-d4bfec5b1ccb?auto=format&fit=crop&w=600&q=60", n: "5/24", zoom: "100%", w: "400", l: "40", color: true },
  { title: "Vue long axe 2-cavités", sub: "Vue latérale", img: "https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&w=600&q=60", n: "2/5", zoom: "120%", w: "350", l: "50", color: true, active: true },
  { title: "Vue long axe 4-cavités", sub: "Vue apicale", img: "https://images.unsplash.com/photo-1581595219315-a187dd40c322?auto=format&fit=crop&w=600&q=60", n: "16/18", zoom: "100%", w: "450", l: "40", color: true },
  { title: "Vue sagittale", sub: "Profil latéral", img: "https://images.unsplash.com/photo-1530026405186-ed1f139313f8?auto=format&fit=crop&w=600&q=60", n: "5/24", zoom: "100%", w: "400", l: "40", color: true },
  { title: "Analyse de perfusion", sub: "Dynamique premier passage", img: "https://images.unsplash.com/photo-1576086213369-97a306d36557?auto=format&fit=crop&w=600&q=60", n: "12/24", zoom: "110%", w: "420", l: "55" },
  { title: "Rendu volumique 3D", sub: "Vue antérieure", img: "https://images.unsplash.com/photo-1551601651-2a8555f1a136?auto=format&fit=crop&w=600&q=60", n: "8/12", zoom: "100%", w: "440", l: "60", color: true },
];

const SEG = [
  { label: "Myocarde VG", value: 45, color: "oklch(0.7 0.2 35)" },
  { label: "Cavité VD", value: 67, color: "oklch(0.65 0.22 300)" },
  { label: "Oreillette gauche", value: 12, color: "oklch(0.78 0.18 150)" },
  { label: "Oreillette droite", value: 89, color: "oklch(0.82 0.16 70)" },
  { label: "Aorte", value: 34, color: "oklch(0.7 0.18 250)" },
  { label: "Artère pulmonaire", value: 78, color: "oklch(0.7 0.2 200)" },
];

const METRICS = [
  { l: "Fraction d'éjection", v: "58", u: "%" },
  { l: "Fréquence cardiaque", v: "72", u: "BPM" },
  { l: "Volume d'éjection", v: "78", u: "ml" },
  { l: "Débit cardiaque", v: "5.6", u: "L/min" },
];

function Bars({ tone = "danger" }: { tone?: "danger" | "success" }) {
  const c = tone === "danger" ? "oklch(0.7 0.2 35)" : "oklch(0.78 0.18 150)";
  return (
    <svg viewBox="0 0 200 30" className="h-7 w-full">
      {Array.from({ length: 70 }).map((_, i) => {
        const h = 6 + Math.abs(Math.sin(i * 0.7) * 18) + (i % 5);
        return <line key={i} x1={i * 2.9} y1={15 - h / 2} x2={i * 2.9} y2={15 + h / 2} stroke={c} strokeWidth="1.2" strokeLinecap="round" />;
      })}
    </svg>
  );
}

function ScanTile(props: (typeof TILES)[number]) {
  return (
    <div className={"group relative aspect-[16/11] overflow-hidden rounded-xl border " + (props.active ? "border-foreground/60 ring-1 ring-foreground/40" : "border-white/10")}>
      <img src={props.img} alt={props.title} className={"h-full w-full object-cover " + (props.color ? "saturate-150" : "grayscale")} />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/70" />
      <div className="absolute left-3 top-3">
        <div className="text-xs font-medium leading-tight">{props.title}</div>
        <div className="text-[10px] text-muted-foreground">{props.sub}</div>
      </div>
      <button className="absolute right-3 top-3 grid h-6 w-6 place-items-center rounded bg-black/40 text-muted-foreground opacity-0 transition group-hover:opacity-100">
        <Maximize2 className="h-3 w-3" />
      </button>
      <div className="absolute bottom-2 left-3 right-3 flex items-center justify-between text-[10px] text-muted-foreground">
        <span>✥ {props.n} · {props.zoom}</span>
        <span>W: {props.w} · L: {props.l}</span>
      </div>
    </div>
  );
}

export default function ImagerieView() {
  const tools = [
    { i: Move, l: "Panoramique", active: true },
    { i: MousePointer2, l: "Sélection" },
    { i: Search, l: "Zoom" },
    { i: Sliders, l: "W/L" },
    { i: Ruler, l: "Mesurer" },
    { i: CircleDot, l: "ROI" },
    { i: Pencil, l: "Croquis" },
    { i: Square, l: "Bloc" },
    { i: Eraser, l: "Effacer" },
  ];
  const [tool, setTool] = useState("Panoramique");

  return (
    <div className="card-surface rounded-2xl p-4">
      {/* breadcrumb */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/5 pb-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <ChevronRight className="h-3 w-3" />
          <span>Scan IRM</span>
          <ChevronRight className="h-3 w-3" />
          <span>2026 groupés</span>
          <ChevronRight className="h-3 w-3" />
          <span className="font-mono text-[10px]">A7c3f9d2-4b8e-1a2f-9d3e-5c8a4e7b1f0a</span>
        </div>
        <div className="flex items-center gap-2">
          <button className="grid h-8 w-8 place-items-center rounded-full bg-accent/30 text-accent-foreground">A</button>
          <button className="grid h-8 w-8 place-items-center rounded-full bg-white/5 text-muted-foreground"><MessageSquare className="h-3.5 w-3.5" /></button>
          <button className="grid h-8 w-8 place-items-center rounded-full bg-white/5 text-muted-foreground"><Bell className="h-3.5 w-3.5" /></button>
          <button className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1.5 text-xs"><Share2 className="h-3 w-3" />Exporter</button>
          <button className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-xs text-primary-foreground"><Send className="h-3 w-3" />Envoyer annotation</button>
        </div>
      </div>

      {/* toolbar */}
      <div className="mt-3 flex flex-wrap items-center gap-1.5 border-b border-white/5 pb-3">
        <button className="inline-flex items-center gap-1 rounded-md bg-white/5 px-2.5 py-1.5 text-xs">cardiac_mri_subject_01.dcm <ChevronRight className="h-3 w-3 rotate-90" /></button>
        <button className="inline-flex items-center gap-1 rounded-md bg-white/5 px-2.5 py-1.5 text-xs text-muted-foreground">Défaut <ChevronRight className="h-3 w-3 rotate-90" /></button>
        <div className="mx-1 h-5 w-px bg-white/10" />
        {tools.map((t) => {
          const Icon = t.i;
          const active = tool === t.l;
          return (
            <button key={t.l} onClick={() => setTool(t.l)} className={"inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs transition " + (active ? "bg-foreground text-background" : "text-muted-foreground hover:bg-white/5")}>
              <Icon className="h-3.5 w-3.5" /> {t.l}
            </button>
          );
        })}
        <div className="ml-auto flex items-center gap-1">
          <button className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-white/5"><Undo2 className="h-3.5 w-3.5" /></button>
          <button className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-white/5"><Redo2 className="h-3.5 w-3.5" /></button>
          <button className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-white/5"><LayoutGrid className="h-3.5 w-3.5" /></button>
        </div>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_320px]">
        {/* scan grid */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {TILES.map((t, i) => <ScanTile key={i} {...t} />)}
        </div>

        {/* AI panel */}
        <aside className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Analyse IA</span>
            <button className="text-muted-foreground"><Maximize2 className="h-3.5 w-3.5" /></button>
          </div>

          <div className="space-y-3">
            <div className="rounded-xl bg-white/[0.03] p-3">
              <div className="flex items-center justify-between text-xs">
                <span>Fonction VG</span>
                <span className="rounded bg-accent/20 px-2 py-0.5 text-[10px] font-medium text-accent">ANORMAL</span>
              </div>
              <Bars tone="danger" />
            </div>
            <div className="rounded-xl bg-white/[0.03] p-3">
              <div className="flex items-center justify-between text-xs">
                <span>Épaisseur paroi</span>
                <span className="rounded bg-success/20 px-2 py-0.5 text-[10px] font-medium text-success">NORMAL</span>
              </div>
              <Bars tone="success" />
            </div>
          </div>

          <dl className="space-y-1.5 rounded-xl bg-white/[0.03] p-3 text-xs">
            {[
              ["Fichier", "cardiac_mri_subject_01.dcm"],
              ["Type de scan", "IRM cardiaque"],
              ["Phase", "Télé-diastole"],
              ["Résolution", "256×256"],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between">
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="truncate pl-2">{v}</dd>
              </div>
            ))}
          </dl>

          <div>
            <div className="mb-2 text-xs font-medium">Mesures cliniques</div>
            <div className="grid grid-cols-2 gap-2">
              {METRICS.map((m) => (
                <div key={m.l} className="rounded-lg bg-white/[0.03] p-2.5">
                  <div className="text-[10px] text-muted-foreground">{m.l}</div>
                  <div className="mt-0.5 text-lg font-light">{m.v}<span className="ml-0.5 text-[10px] text-muted-foreground">{m.u}</span></div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-2 text-xs font-medium">Confiance segmentation</div>
            <ul className="space-y-2">
              {SEG.map((s) => (
                <li key={s.label} className="flex items-center gap-2 text-xs">
                  <input type="checkbox" defaultChecked className="accent-foreground" />
                  <span className="flex-1 truncate">{s.label}</span>
                  <div className="h-1.5 w-16 overflow-hidden rounded-full bg-white/10">
                    <div className="h-full rounded-full" style={{ width: s.value + "%", background: s.color }} />
                  </div>
                  <span className="w-8 text-right text-muted-foreground">{s.value}%</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="mb-2 text-xs font-medium">Contraste — chronologie</div>
            <div className="space-y-2 rounded-xl bg-white/[0.03] p-3">
              <div className="text-[10px] text-muted-foreground">Artériel</div>
              <svg viewBox="0 0 200 40" className="h-10 w-full">
                <path d="M0 25 C 30 5, 60 35, 90 18 S 150 30, 200 12" stroke="oklch(0.7 0.2 35)" fill="none" strokeWidth="1.5" />
                <path d="M0 30 C 40 22, 80 12, 120 28 S 180 18, 200 24" stroke="oklch(0.7 0.18 250)" fill="none" strokeWidth="1.5" />
              </svg>
              <div className="flex justify-between text-[9px] text-muted-foreground"><span>-0.14</span><span>0.00</span><span>0.18</span><span>0.43</span></div>
              <div className="mt-2 text-[10px] text-muted-foreground">Veineux</div>
              <svg viewBox="0 0 200 40" className="h-10 w-full">
                <path d="M0 28 C 35 12, 70 30, 105 20 S 170 28, 200 16" stroke="oklch(0.7 0.2 35)" fill="none" strokeWidth="1.5" />
                <path d="M0 22 C 40 30, 90 14, 130 24 S 180 22, 200 28" stroke="oklch(0.7 0.18 250)" fill="none" strokeWidth="1.5" />
              </svg>
              <div className="flex justify-between text-[9px] text-muted-foreground"><span>-0.28</span><span>0.00</span><span>0.35</span><span>0.77</span></div>
            </div>
          </div>
        </aside>
      </div>

      {/* timeline */}
      <div className="mt-4 flex items-center gap-3 border-t border-white/5 pt-3 text-xs">
        <button className="text-muted-foreground"><SkipBack className="h-4 w-4" /></button>
        <button className="grid h-8 w-8 place-items-center rounded-full bg-foreground text-background"><Play className="h-3.5 w-3.5" /></button>
        <button className="text-muted-foreground"><SkipForward className="h-4 w-4" /></button>
        <button className="text-muted-foreground"><Repeat className="h-4 w-4" /></button>
        <span className="text-muted-foreground">24 / 67</span>
        <div className="relative mx-2 h-1 flex-1 rounded-full bg-white/10">
          <div className="absolute inset-y-0 left-0 w-[35%] rounded-full bg-foreground/70" />
          <div className="absolute -top-1.5 h-4 w-4 -translate-x-1/2 rounded-full border-2 border-background bg-foreground" style={{ left: "35%" }} />
        </div>
        <span className="text-muted-foreground">Phase</span>
        <span className="rounded-md bg-white/5 px-2 py-1">Mi-diastole</span>
        <span className="text-muted-foreground">Vitesse</span>
        <span className="rounded-md bg-white/5 px-2 py-1">1.00×</span>
      </div>
    </div>
  );
}
